import * as Cookies from "es-cookie"
import { dangerToast, ToastOptionsGravity, ToastOptionsPosition } from "./toast"

declare var $: any

const ajax = $.ajax

export enum RequestType {
  GET = "get",
  POST = "post",
  PUT = "put",
  DELETE = "delete",
}

export enum RequestDataType {
  XML = "xml",
  JSON = "json",
  JSONP = "jsonp",
  SCRIPT = "script",
  HTML = "html",
  TEXT = "text",
}

export enum RequestContentType {
  XML = "text/xml",
  JSON = "application/json",
  JSONP = "application/jsonp",
  HTML = "text/html",
  TEXT = "text/plain",
}

export interface RequestOptions {
  params?: { [key: string]: any }
  data?: { [key: string]: any }
  type: RequestType
  dataType?: RequestDataType
  contentType?: RequestContentType
  silent?: boolean
  skipAuthorization?: boolean
  track?: boolean
}

export function resolveUrl(path: string) {
  const baseUrl = document.getElementById("base").getAttribute("value")
  const language = document.documentElement.lang
  return `${baseUrl}/${language}${path}`
}

function request(path: string, options: RequestOptions) {
  return new Promise((resolve, reject) => {
    const settings: any = {}
    settings.type = options.type
    if ([RequestType.GET, RequestType.DELETE].includes(options.type)) {
      settings.data = options.params ?? {}
    } else if ([RequestType.POST, RequestType.PUT].includes(options.type)) {
      settings.data = options.data ?? {}
    }
    settings.dataType = options.dataType ?? RequestDataType.JSON
    settings.headers = {
      accept: options.contentType ?? RequestContentType.JSON,
    }
    if (options.skipAuthorization) {
      settings.headers["skip-authorization"] = "on"
    }
    if (options.track) {
      settings.headers["track"] = "on"
    }
    settings.url = resolveUrl(path)
    settings.beforeSend = (xhr) => {
      const token =
        Cookies?.get("ghToken")?.replace(
          /(.*)\{((.+?)\"(.+?)\"(.+?))+?\}/g,
          (fullText, p0, p1, p2, value, p3) => value,
        ) ?? null
      if (token) {
        xhr.setRequestHeader("Authorization", `Bearer ${token}`)
      }
    }
    settings.complete = (XMLHttpRequest, textStatus) => {
      if (textStatus.toLowerCase() === "success") {
        resolve({
          data: XMLHttpRequest.responseJSON,
          header: XMLHttpRequest.getResponseHeader,
        })
      }
    }
    settings.error = (jqXHR: any, exception: any) => {
      if (jqXHR.status === 200) {
        resolve({
          data: jqXHR.responseText,
          header: jqXHR.getResponseHeader,
        })
      } else {
        let msg = ""
        if (jqXHR.responseJSON && jqXHR.responseJSON.title) {
          msg = jqXHR.responseJSON.title
        } else if (jqXHR.status === 0) {
          msg = "Not connect, Verify Network."
        } else if (jqXHR.status == 404) {
          msg = "Requested page not found. [404]"
        } else if (jqXHR.status == 500) {
          msg = "Internal Server Error [500]."
        } else if (exception === "parsererror") {
          msg = "Requested JSON parse failed."
        } else if (exception === "timeout") {
          msg = "Time out error."
        } else if (exception === "abort") {
          msg = "Ajax request aborted."
        } else {
          msg = "Uncaught Error : " + jqXHR.responseText
        }
        if (!options.silent) {
          apiError({ msg, xhr: jqXHR, exception })
        }
        reject({ msg, xhr: jqXHR, exception })
      }
    }
    ajax(settings)
  })
}

class Api {
  #silent = false
  #skipAuthorization = false
  #track = false

  silent() {
    this.#silent = true
    return this
  }

  skipAuthorization() {
    this.#skipAuthorization = true
    return this
  }

  track() {
    this.#track = true
    return this
  }

  get(path: string, options: any = {}) {
    return request(path, {
      ...options,
      ...{
        type: RequestType.GET,
        silent: this.#silent,
        skipAuthorization: this.#skipAuthorization,
        track: this.#track,
      },
    })
  }

  post(path: string, data: { [key: string]: any }) {
    return request(path, {
      type: RequestType.POST,
      data,
      silent: this.#silent,
      skipAuthorization: this.#skipAuthorization,
      track: this.#track,
    })
  }

  put(path: string, data: { [key: string]: any }) {
    return request(path, {
      type: RequestType.PUT,
      data,
      silent: this.#silent,
      skipAuthorization: this.#skipAuthorization,
      track: this.#track,
    })
  }

  delete(path: string) {
    return request(path, {
      type: RequestType.DELETE,
      silent: this.#silent,
      skipAuthorization: this.#skipAuthorization,
      track: this.#track,
    })
  }
}

export function api() {
  return new Api()
}

export function apiError(data: any) {
  const { msg, xhr, exception } = data
  let text = []
  for (const name in xhr.responseJSON.details) {
    text = [...text, ...xhr.responseJSON.details[name]]
  }
  let message = `<p>${msg}</p>`
  if (text.length > 0) {
    message += `<ul><li>${text.join(`</li><li>`)}</li></ul>`
  }
  dangerToast({
    title: exception,
    text: message,
    duration: 3000,
    closable: false,
    gravity: ToastOptionsGravity.bottom,
    position: ToastOptionsPosition.right,
  })
}
